import { useEffect, useState } from "react";
import { useBlocker } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { pickEffective, type ProductPriceDto } from "@hisense/shared";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { SearchableFilter } from "@/components/ui/SearchableFilter";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { useCustomers, useProductPrices, useSetPrice } from "@/api/hooks";
import { getLocalizedError } from "@/lib/api";
import { parseMoneyMajor } from "@/lib/moneyInput";
import { formatUnitPrice } from "@/domain/money";
import { formatDateTime } from "@/domain/datetime";

export function CustomerPricesSection({ productId, currency, sellingPrice, canSet, onDirtyChange }: {
  productId: string; currency: string; sellingPrice: number | null; canSet: boolean; onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const prices = useProductPrices(productId);
  const customers = useCustomers({});
  const setPrice = useSetPrice(productId);
  const [customerId, setCustomerId] = useState("");
  const [amount, setAmount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);
  const [confirm, setConfirm] = useState<{ price: number; customerId: string; name: string; before: number | null; beforeCurrency: string } | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const dirty = amount.trim() !== "";
  useEffect(() => { onDirtyChange?.(dirty || setPrice.isPending); return () => onDirtyChange?.(false); }, [dirty, setPrice.isPending, onDirtyChange]);
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    (dirty || setPrice.isPending) && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));
  useEffect(() => {
    if (!dirty && !setPrice.isPending) return;
    const beforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty, setPrice.isPending]);

  const rows = prices.data ?? [];
  const current = rows.filter((p) => p.customerId != null && p.effectiveTo === null);
  const history = rows.filter((p) => p.customerId != null && p.effectiveTo != null);
  const customerOptions = (customers.data?.data ?? []).map((c) => ({ value: c.id, label: c.companyName }));
  const reviewPrice = () => {
    setError(null); setApplied(false);
    const selected = customerOptions.find((c) => c.value === customerId);
    const parsed = parseMoneyMajor(amount);
    if (!selected) { setError(t("pricing.selectCustomer")); return; }
    if (parsed.invalid || parsed.minor === undefined || !Number.isSafeInteger(parsed.minor)) { setError(t("pricing.invalidPrice")); return; }
    const effective = pickEffective(rows, { customerId, asOf: Date.now(), fallbackCurrency: currency });
    setConfirm({ price: parsed.minor, customerId, name: selected.label,
      before: effective.source === "none" ? sellingPrice : effective.price, beforeCurrency: effective.currency });
  };
  const applyPrice = async () => {
    if (!confirm || setPrice.isPending) return;
    try {
      await setPrice.mutateAsync({ price: confirm.price, customerId: confirm.customerId, currency });
      setAmount(""); setApplied(true); setConfirm(null); setError(null);
    } catch (err) { setConfirm(null); setError(getLocalizedError(err, t, "pricing.errorSet")); }
  };
  const renderPrices = (entries: ProductPriceDto[]) => <TableShell>
    <thead><tr><Th>{t("pricing.customer")}</Th><Th align="right">{t("pricing.price")}</Th><Th>{t("pricing.effective")}</Th><Th>{t("pricing.status")}</Th></tr></thead>
    <tbody>{entries.map((p) => <Tr key={p.id}>
      <Td>{p.customerName ?? p.customerId}</Td>
      <Td align="right" className="font-mono tabular">{formatUnitPrice(p.price, p.currency)} {p.currency}</Td>
      <Td className="font-mono tabular text-xs">{formatDateTime(p.effectiveFrom, i18n.language)} → {p.effectiveTo ? formatDateTime(p.effectiveTo, i18n.language) : "—"}</Td>
      <Td><Badge tone={p.effectiveTo === null ? "ok" : "neutral"} outline>{t(p.effectiveTo === null ? "pricing.current" : "pricing.closed")}</Badge></Td>
    </Tr>)}</tbody>
  </TableShell>;
  return <section id="customer-prices" className="scroll-mt-5 space-y-4">
    <h2 className="text-lg font-semibold text-ink">{t("pricing.title")}</h2>
    <p className="text-sm text-ink-soft">{t("pricing.hint")}</p>
    <p className="rounded-sm border border-info/20 bg-info-tint px-3 py-2 text-sm text-ink">{t("productWorkspace.pricingNote")}</p>
    {prices.isLoading ? <LoadingState label={t("pricing.loading")} /> : prices.error ?
      <ErrorState message={t("productWorkspace.priceLoadError")} onRetry={() => void prices.refetch()} /> : <>
      {canSet && <div className="space-y-3">
        {customers.error ? <ErrorState message={t("productWorkspace.customersLoadError")} onRetry={() => void customers.refetch()} /> :
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-52 flex-1"><FieldLabel>{t("pricing.customer")}</FieldLabel>
              <SearchableFilter label={t("pricing.customer")} allLabel={t("pricing.selectCustomer")} includeAll={false}
                searchPlaceholder={t("common.search")} className="w-full" value={customerId} options={customerOptions}
                onChange={(value) => { if (!setPrice.isPending) { setCustomerId(value); setApplied(false); } }} />
            </div>
            <div className="w-44"><FieldLabel htmlFor="pr-amt" hint={currency}>{t("pricing.price")}</FieldLabel>
              <Input id="pr-amt" inputMode="decimal" disabled={setPrice.isPending} value={amount}
                onChange={(e) => { setAmount(e.target.value); setApplied(false); }}
                placeholder={sellingPrice != null ? formatUnitPrice(sellingPrice, currency, false) : "0.0000"} />
            </div>
            <Button type="button" disabled={setPrice.isPending || prices.isFetching || !customerId || !amount.trim()} onClick={reviewPrice}>{t("productWorkspace.applyPrice")}</Button>
            {dirty && <Button variant="ghost" disabled={setPrice.isPending} onClick={() => setConfirmClear(true)}>{t("productWorkspace.cancelPrice")}</Button>}
          </div>}
        {error && <p role="alert" className="text-sm text-danger">{error}</p>}
        {applied && <p role="status" className="rounded-sm bg-ok-tint px-3 py-2 text-sm text-ok">{t("productWorkspace.priceApplied")}</p>}
      </div>}
      <h3 className="text-sm font-semibold text-ink">{t("productWorkspace.currentPrices")}</h3>
      {current.length ? renderPrices(current) : <p className="text-sm text-ink-soft">{t("pricing.none")}</p>}
      <details><summary className="cursor-pointer text-sm font-medium text-brand">{t("productWorkspace.priceHistory")} <span className="font-mono tabular">({history.length})</span></summary>
        <div className="mt-3">{history.length ? renderPrices(history) : <p className="text-sm text-ink-soft">{t("productWorkspace.noHistory")}</p>}</div>
      </details>
    </>}
    <ConfirmDialog open={!!confirm && blocker.state !== "blocked"} title={t("productWorkspace.confirmPriceTitle")}
      description={confirm ? t("productWorkspace.confirmPriceBody", { customer: confirm.name,
        before: confirm.before == null ? t("productWorkspace.noPrice") : `${formatUnitPrice(confirm.before, confirm.beforeCurrency)} ${confirm.beforeCurrency}`,
        after: `${formatUnitPrice(confirm.price, currency)} ${currency}` }) : ""}
      confirmLabel={t("productWorkspace.applyPrice")} cancelLabel={t("common.keepEditing")} tone="warn" busy={setPrice.isPending}
      onCancel={() => setConfirm(null)} onConfirm={() => void applyPrice()} />
    <ConfirmDialog open={confirmClear || blocker.state === "blocked"} title={t("common.unsavedTitle")}
      description={t("common.unsavedDescription")} confirmLabel={t("common.discardChanges")} cancelLabel={t("common.keepEditing")}
      tone="warn" busy={setPrice.isPending}
      onCancel={() => { setConfirmClear(false); if (blocker.state === "blocked") blocker.reset(); }}
      onConfirm={() => { setAmount(""); setConfirm(null); setConfirmClear(false); if (blocker.state === "blocked") blocker.proceed(); }} />
  </section>;
}
